/**
 * PhoneNumberInput
 *
 * A controlled phone number input that:
 *   - Displays a country dial-code selector (flag + +CC prefix)
 *   - Formats the subscriber number as digits-only
 *   - Outputs the value in E.164 format: +[cc][number]
 *   - Validates the minimum digit length (7 digits after dial code)
 *
 * @example
 * <PhoneNumberInput
 *   value={phone}
 *   onChange={setPhone}
 *   error={errors.phone_number?.message}
 * />
 */
import { useState, useId } from 'react'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

interface Country {
  code: string
  dial: string
  flag: string
  name: string
}

const COUNTRIES: Country[] = [
  { code: 'GH', dial: '233', flag: '🇬🇭', name: 'Ghana' },
  { code: 'NG', dial: '234', flag: '🇳🇬', name: 'Nigeria' },
  { code: 'KE', dial: '254', flag: '🇰🇪', name: 'Kenya' },
  { code: 'ZA', dial: '27',  flag: '🇿🇦', name: 'South Africa' },
  { code: 'US', dial: '1',   flag: '🇺🇸', name: 'United States' },
  { code: 'GB', dial: '44',  flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'CA', dial: '1',   flag: '🇨🇦', name: 'Canada' },
  { code: 'AU', dial: '61',  flag: '🇦🇺', name: 'Australia' },
  { code: 'DE', dial: '49',  flag: '🇩🇪', name: 'Germany' },
  { code: 'FR', dial: '33',  flag: '🇫🇷', name: 'France' },
  { code: 'IN', dial: '91',  flag: '🇮🇳', name: 'India' },
  { code: 'AE', dial: '971', flag: '🇦🇪', name: 'UAE' },
]

const DEFAULT_COUNTRY = COUNTRIES[0]!

function toE164(dialCode: string, subscriber: string): string {
  const digits = subscriber.replace(/\D/g, '')
  return digits ? `+${dialCode}${digits}` : ''
}

/** Parse an E.164 string back into country + subscriber parts */
export function parseE164(e164: string): { country: Country; subscriber: string } {
  if (!e164) return { country: DEFAULT_COUNTRY, subscriber: '' }
  if (!e164.startsWith('+')) {
    return { country: DEFAULT_COUNTRY, subscriber: e164.replace(/\D/g, '') }
  }
  const raw = e164.slice(1)
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)
  for (const c of sorted) {
    if (raw.startsWith(c.dial)) {
      return { country: c, subscriber: raw.slice(c.dial.length) }
    }
  }
  return { country: DEFAULT_COUNTRY, subscriber: raw }
}

interface PhoneNumberInputProps {
  value: string
  onChange: (e164: string) => void
  onBlur?: () => void
  label?: string
  error?: string
  disabled?: boolean
  required?: boolean
  className?: string
}

export function PhoneNumberInput({
  value,
  onChange,
  onBlur,
  label = 'Phone number',
  error,
  disabled = false,
  required = false,
  className,
}: PhoneNumberInputProps) {
  const id = useId()
  const errorId = `${id}-error`

  const parsed = parseE164(value)
  const [country, setCountry] = useState<Country>(parsed.country)
  const [subscriber, setSubscriber] = useState(parsed.subscriber)

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = COUNTRIES.find((c) => c.code === e.target.value) ?? DEFAULT_COUNTRY
    setCountry(next)
    onChange(toE164(next.dial, subscriber))
  }

  const handleSubscriberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '')
    setSubscriber(digits)
    onChange(toE164(country.dial, digits))
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label
          htmlFor={id}
          className={
            required
              ? "after:ml-0.5 after:text-destructive after:content-['*']"
              : ''
          }
        >
          {label}
        </Label>
      )}

      <div className="flex">
        <select
          value={country.code}
          onChange={handleCountryChange}
          disabled={disabled}
          aria-label="Country dial code"
          className={cn(
            'flex h-9 items-center rounded-l-md border border-r-0 border-input bg-muted px-2 text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:z-10',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive',
          )}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} +{c.dial}
            </option>
          ))}
        </select>

        <input
          id={id}
          type="tel"
          inputMode="numeric"
          value={subscriber}
          onChange={handleSubscriberChange}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          placeholder="000 000 0000"
          aria-describedby={error ? errorId : undefined}
          aria-invalid={!!error}
          className={cn(
            'flex h-9 flex-1 rounded-r-md border border-input bg-background px-3 text-sm',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive focus-visible:ring-destructive',
          )}
        />
      </div>

      {error && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
